import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  ResponsiveContainer, ComposedChart, Line, Area,
  XAxis, YAxis, CartesianGrid,
  ReferenceLine, ReferenceArea,
} from 'recharts';
import { LAP_COLORS, getLapColor, TB_LAP_INDEX, TB_COLOR } from '../LapSelector/LapSelector';
import { subscribeExtent } from '../../utils/chartExtentBus';
import { buildLookup, normalizeLookup, interpolateRelTime, pickBaseLapMulti, lapDisplayNumber } from '../../utils/lapDelta';
import PanelHeader from '../WindowFrame/headerSlot';
import HelpButton from '../HelpButton/HelpButton';
import { useCornerHighlight } from '../../hooks/useCornerHighlight';
import { subscribeZoom, publishZoom } from '../../utils/chartZoomBus';
import { useTouchChartGestures } from '../../hooks/useTouchChartGestures';
import { usePanOnWheel } from '../../hooks/usePanOnWheel';
import styles from './TimeDeltaTrace.module.css';
import { useTheme } from '../../contexts/ThemeContext';
import { lastCursor } from '../../utils/chartCursor';
import { convertDistanceFt, getDistanceUnit, TOOLTIP_FONT_SCALES } from '../../utils/unitConvert';
import { exportChartPng } from '../../utils/chartExport';
import { arrayExtent, arrayMin, arrayMax } from '../../utils/extent';

const M_TO_FT = 3.28084;
const MAX_PTS  = 5000;

function fmtFt(ft, prefs = {}) {
  return Math.round(convertDistanceFt(ft, prefs)).toLocaleString('en-US') + ' ' + getDistanceUnit(prefs);
}

function fmtRelTime(secs) {
  if (secs == null || isNaN(secs)) return '—';
  const neg = secs < 0;
  const abs = Math.abs(secs);
  const m = Math.floor(abs / 60);
  const s = (abs % 60).toFixed(3).padStart(6, '0');
  return `${neg ? '-' : ''}${m}:${s}`;
}

// Must mirror chartMargin.top and (chartMargin.bottom + XAxis ticks/label height)
const CHART_TOP    = 8;
const CHART_BOTTOM = 24;

// ── Base-lap picker — badge in the title bar, popover portaled to <body> ─────
function BasePicker({
  baseLapInfo, baseOverride, onChange,
  lapTraces, selectedLaps, laps,
  compareDrivers = [],
}) {
  const [open, setOpen]     = useState(false);
  const [anchor, setAnchor] = useState(null);
  const popRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!baseLapInfo) return null;

  const opts = [];
  (selectedLaps || []).forEach(idx => {
    const tr = lapTraces?.[idx];
    if (tr) opts.push({ session: 1, lapIndex: idx, tr });
  });
  compareDrivers.forEach(d => {
    (d.selectedLaps || []).forEach(idx => {
      const tr = d.lapTraces?.[idx];
      if (tr) opts.push({ session: d.slot + 2, lapIndex: idx, tr, laps: d.laps });
    });
  });

  const optLabel = (o) => {
    if (o.lapIndex === TB_LAP_INDEX) return `TB — ${o.tr.lap_time_str || ''}`;
    const num = lapDisplayNumber(o.session === 1 ? laps : o.laps, o.lapIndex, o.tr.lap_number);
    return `${o.session === 1 ? '' : `D${o.session} `}L${num} — ${o.tr.lap_time_str || ''}`;
  };
  const isActive = (o) =>
    baseOverride && baseOverride.session === o.session && baseOverride.lapIndex === o.lapIndex;

  const badge = (
    <>Base: {baseLapInfo.label}{baseOverride ? '' : ' (auto)'}{onChange ? ' ▾' : ''}</>
  );

  if (!onChange) {
    return (
      <span className={styles.baseLabel} style={{ borderColor: baseLapInfo.color, color: baseLapInfo.color }}>
        {badge}
      </span>
    );
  }

  return (
    <>
      <button
        ref={btnRef}
        className={styles.baseLabel}
        style={{ borderColor: baseLapInfo.color, color: baseLapInfo.color }}
        onClick={(e) => {
          setAnchor(e.currentTarget.getBoundingClientRect());
          setOpen(v => !v);
        }}
        title="Choose the base (reference) lap — also drives the map's Delta ribbon"
      >
        {badge}
      </button>
      {open && anchor && createPortal(
        <div
          ref={popRef}
          className={styles.basePopover}
          style={{
            left: Math.min(anchor.left, window.innerWidth - 190),
            top:  anchor.bottom + 6,
          }}
        >
          <div className={styles.basePopoverHdr}>Base lap</div>
          <button
            className={`${styles.baseOpt} ${!baseOverride ? styles.baseOptActive : ''}`}
            onClick={() => { onChange(null); setOpen(false); }}
          >
            Auto — fastest selected
          </button>
          {opts.map(o => (
            <button
              key={`${o.session}-${o.lapIndex}`}
              className={`${styles.baseOpt} ${isActive(o) ? styles.baseOptActive : ''}`}
              onClick={() => { onChange({ session: o.session, lapIndex: o.lapIndex }); setOpen(false); }}
            >
              {optLabel(o)}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

function downsample(arr, maxPts) {
  if (arr.length <= maxPts) return arr;
  const step = arr.length / maxPts;
  return Array.from({ length: maxPts }, (_, i) => arr[Math.round(i * step)]);
}

function findNearest(points, distFt) {
  if (!points?.length) return null;
  let best = points[0];
  let bestDiff = Math.abs(points[0].dist_ft - distFt);
  for (let i = 1; i < points.length; i++) {
    const diff = Math.abs(points[i].dist_ft - distFt);
    if (diff < bestDiff) { best = points[i]; bestDiff = diff; }
  }
  return best;
}


function buildDeltaSeries(baseLookup, otherTrace, zoomDomain) {
  const raw = otherTrace.points.filter(p => p.dist_m != null && p.time != null && p.lat && p.lon);
  if (!raw.length || !baseLookup.length) return [];

  const t0 = raw[0].time;

  const source = zoomDomain
    ? raw.filter(p => {
        const ft = p.dist_m * M_TO_FT;
        return ft >= zoomDomain[0] && ft <= zoomDomain[1];
      })
    : raw;

  const sampled = downsample(source.length > 0 ? source : raw, MAX_PTS);

  return sampled.map(p => {
    const otherRel = p.time - t0;
    const baseRel  = interpolateRelTime(baseLookup, p.dist_m);
    if (baseRel == null) return null;
    return {
      dist_ft: Math.round(p.dist_m * M_TO_FT),
      delta:   Math.round((otherRel - baseRel) * 1000) / 1000,
      lat: p.lat,
      lon: p.lon,
    };
  }).filter(Boolean);
}

// ── Following dots — one per series at the hovered delta value ───────────────
function HoverDots({ data, yMin, yMax, wrapHeight }) {
  if (!data?.entries?.length || wrapHeight == null) return null;
  const yRange = yMax - yMin;
  const chartH = wrapHeight - CHART_TOP - CHART_BOTTOM;
  if (yRange === 0 || chartH <= 0) return null;

  return (
    <>
      {data.entries.map(e => {
        if (e.delta == null) return null;
        const yFrac = 1 - (e.delta - yMin) / yRange;
        const yPx   = CHART_TOP + yFrac * chartH;
        return (
          <div
            key={e.key}
            style={{
              position:      'absolute',
              left:          data.x - 5,
              top:           yPx - 5,
              width:         10,
              height:        10,
              borderRadius:  '50%',
              background:    e.color,
              border:        '2px solid #fff',
              pointerEvents: 'none',
              zIndex:        15,
              boxShadow:     `0 0 4px ${e.color}88`,
            }}
          />
        );
      })}
    </>
  );
}

// ── Imperative hover tooltip ──────────────────────────────────────────────────
function HoverTooltip({ data, wrapWidth, anchoredTooltip, unitPrefs = {} }) {
  if (!data?.entries?.length) return null;
  const { x, distFt, entries, side } = data;
  const scale = TOOLTIP_FONT_SCALES[unitPrefs.tooltipFontScale] ?? 1;

  const TOOLTIP_W = Math.round(250 * scale);
  let leftVal, rightVal;
  if (anchoredTooltip) {
    leftVal  = side === 'left'  ? 50 : undefined;
    rightVal = side === 'right' ? 10 : undefined;
  } else {
    const flipLeft = x > (wrapWidth ?? 600) * 0.62;
    leftVal  = flipLeft ? undefined : x + 14;
    rightVal = flipLeft ? (wrapWidth - x + 8) : undefined;
  }

  return (
    <div
      className={styles.tooltip}
      style={{
        position:      'absolute',
        top:           10,
        left:          leftVal,
        right:         rightVal,
        pointerEvents: 'none',
        zIndex:        20,
        maxWidth:      TOOLTIP_W,
        fontSize:      `${(0.78 * scale).toFixed(3)}rem`,
      }}
    >
      <div className={styles.tooltipDist}>
        {(convertDistanceFt(distFt, unitPrefs) / 1000).toFixed(2)}k {getDistanceUnit(unitPrefs)}
      </div>
      {entries.map(e => {
        const pos = e.delta > 0;
        return (
          <div key={e.key} className={styles.tooltipRow} style={{ borderLeftColor: e.color }}>
            <span className={styles.tooltipLabel}>
              {e.driverName && <span className={styles.tooltipDriver}>{e.driverName} · </span>}
              {e.label}
            </span>
            <span
              className={styles.tooltipVal}
              style={{ color: pos ? '#e87f4d' : e.delta < 0 ? '#44cc88' : '#888' }}
            >
              {pos ? '+' : ''}{e.delta.toFixed(3)}
              <span className={styles.tooltipUnit}> s</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Column header with hover tooltip ─────────────────────────────────────────
// Uses position:fixed so the tooltip escapes any overflow:hidden parent.
function ColTip({ children, tip, className }) {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);

  const handleMouseEnter = () => {
    if (!ref.current) return;
    const r   = ref.current.getBoundingClientRect();
    const TIP = 180;
    const raw = r.left + r.width / 2 - TIP / 2;
    const left = Math.max(6, Math.min(window.innerWidth - TIP - 6, raw));
    setPos({ bottom: window.innerHeight - r.top + 6, left });
  };

  return (
    <span
      ref={ref}
      className={`${styles.colTip}${className ? ` ${className}` : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setPos(null)}
    >
      {children}
      {pos && createPortal(
        <span
          className={styles.colTipBox}
          style={{ position: 'fixed', bottom: pos.bottom, left: pos.left }}
        >
          {tip}
        </span>,
        document.body
      )}
    </span>
  );
}

// ── Measure readout ───────────────────────────────────────────────────────────
function MeasureReadout({ points, series, baseLookup, baseLapInfo, unitPrefs = {} }) {
  if (points.length < 2) return null;
  const [ptA, ptB] = points;

  const distSpanFt = ptB.distFt - ptA.distFt;
  const baseTimeA  = interpolateRelTime(baseLookup, ptA.distFt / M_TO_FT);
  const baseTimeB  = interpolateRelTime(baseLookup, ptB.distFt / M_TO_FT);
  const baseElapsed = baseTimeA != null && baseTimeB != null ? baseTimeB - baseTimeA : null;

  return (
    <div className={styles.measureReadout}>
      <div className={styles.measureDist}>
        <span className={styles.measureDistA}>A: {fmtFt(ptA.distFt, unitPrefs)}</span>
        <span style={{ color: '#2e2e2e' }}> → </span>
        <span className={styles.measureDistB}>B: {fmtFt(ptB.distFt, unitPrefs)}</span>
        <span className={styles.measureDistSpan}>
          {' · '}span: {fmtFt(Math.abs(distSpanFt), unitPrefs)}
        </span>
      </div>
      <table className={styles.measureTable}>
        <thead>
          <tr>
            <th>Lap</th>
            <th className={styles.measureColA}>
              <ColTip tip="Distance from lap start to point A.">Dist @ A</ColTip>
            </th>
            <th className={styles.measureColB}>
              <ColTip tip="Distance from lap start to point B.">Dist @ B</ColTip>
            </th>
            <th className={styles.measureColElapsed}>
              <ColTip tip="Distance between point A and point B.">Dist Span</ColTip>
            </th>
            <th className={styles.measureColA}>
              <ColTip tip="Elapsed lap time at point A (from lap start).">Time @ A</ColTip>
            </th>
            <th className={styles.measureColB}>
              <ColTip tip="Elapsed lap time at point B (from lap start).">Time @ B</ColTip>
            </th>
            <th className={styles.measureColElapsed}>
              <ColTip tip="Time this lap took to travel from A to B.">Time Span</ColTip>
            </th>
            <th className={styles.measureColA}>
              <ColTip tip="Time delta vs. base lap at point A. Negative = ahead of base lap.">Δ @ A</ColTip>
            </th>
            <th className={styles.measureColB}>
              <ColTip tip="Time delta vs. base lap at point B. Negative = ahead of base lap.">Δ @ B</ColTip>
            </th>
            <th>
              <ColTip tip="Change in time delta from A→B. Green = time gained vs. base lap; orange = time lost.">Gain / Loss</ColTip>
            </th>
          </tr>
        </thead>
        <tbody>
          {baseLapInfo && (
            <tr key="base">
              <td className={styles.measureLap} style={{ borderLeftColor: baseLapInfo.color }}>
                {baseLapInfo.label} <span className={styles.baseTag}>base</span>
              </td>
              <td className={styles.measureColA}>
                <ColTip tip={`Base lap reached point A after ${fmtFt(ptA.distFt, unitPrefs)} from lap start.`}>
                  {fmtFt(ptA.distFt, unitPrefs)}
                </ColTip>
              </td>
              <td className={styles.measureColB}>
                <ColTip tip={`Base lap reached point B after ${fmtFt(ptB.distFt, unitPrefs)} from lap start.`}>
                  {fmtFt(ptB.distFt, unitPrefs)}
                </ColTip>
              </td>
              <td className={styles.measureColElapsed}>
                <ColTip tip={`Base lap traveled ${fmtFt(Math.abs(distSpanFt), unitPrefs)} between A and B.`}>
                  {fmtFt(Math.abs(distSpanFt), unitPrefs)}
                </ColTip>
              </td>
              <td className={styles.measureColA}>
                <ColTip tip={`Base lap had been on track for ${fmtRelTime(baseTimeA)} at point A.`}>
                  {fmtRelTime(baseTimeA)}
                </ColTip>
              </td>
              <td className={styles.measureColB}>
                <ColTip tip={`Base lap had been on track for ${fmtRelTime(baseTimeB)} at point B.`}>
                  {fmtRelTime(baseTimeB)}
                </ColTip>
              </td>
              <td className={styles.measureColElapsed}>
                <ColTip tip={`Base lap covered the A→B section in ${baseElapsed != null ? baseElapsed.toFixed(3) + ' s' : '—'}.`}>
                  {baseElapsed != null ? baseElapsed.toFixed(3) + ' s' : '—'}
                </ColTip>
              </td>
              <td className={styles.measureEven}>—</td>
              <td className={styles.measureEven}>—</td>
              <td className={styles.measureEven}>—</td>
            </tr>
          )}
          {series.map(s => {
            const eA = ptA.entries.find(e => e.key === s.key);
            const eB = ptB.entries.find(e => e.key === s.key);
            if (!eA || !eB) return null;
            const diff = eB.delta - eA.delta;
            const diffStr = (diff > 0 ? '+' : '') + diff.toFixed(3) + ' s';
            const diffClass = diff < -0.001 ? styles.measureGain : diff > 0.001 ? styles.measureLoss : styles.measureEven;
            const lapName = (s.driverName ? `${s.driverName} · ` : '') + s.label;

            const timeAtA   = baseTimeA  != null ? baseTimeA  + eA.delta : null;
            const timeAtB   = baseTimeB  != null ? baseTimeB  + eB.delta : null;
            const elapsed   = baseElapsed != null ? baseElapsed + diff    : null;

            const lapDistA    = eA.dist_ft ?? ptA.distFt;
            const lapDistB    = eB.dist_ft ?? ptB.distFt;
            const lapDistSpan = lapDistB - lapDistA;

            const aTip = eA.delta > 0.001
              ? `At point A, ${lapName} is ${eA.delta.toFixed(3)} s behind the base lap.`
              : eA.delta < -0.001
                ? `At point A, ${lapName} is ${Math.abs(eA.delta).toFixed(3)} s ahead of the base lap.`
                : `At point A, ${lapName} is even with the base lap.`;
            const bTip = eB.delta > 0.001
              ? `At point B, ${lapName} is ${eB.delta.toFixed(3)} s behind the base lap.`
              : eB.delta < -0.001
                ? `At point B, ${lapName} is ${Math.abs(eB.delta).toFixed(3)} s ahead of the base lap.`
                : `At point B, ${lapName} is even with the base lap.`;
            const gainLossTip = diff < -0.001
              ? `${lapName} gained ${Math.abs(diff).toFixed(3)} s on the base lap between A and B.`
              : diff > 0.001
                ? `${lapName} lost ${diff.toFixed(3)} s to the base lap between A and B.`
                : `${lapName} held even with the base lap between A and B.`;
            const elapsedTip = elapsed != null
              ? `${lapName} covered the A→B section in ${elapsed.toFixed(3)} s.`
              : 'Elapsed time unavailable.';

            return (
              <tr key={s.key}>
                <td className={styles.measureLap} style={{ borderLeftColor: s.color }}>
                  {lapName}
                </td>
                <td className={styles.measureColA}>
                  <ColTip tip={`${lapName} reached point A after traveling ${fmtFt(lapDistA, unitPrefs)} from lap start.`}>
                    {fmtFt(lapDistA, unitPrefs)}
                  </ColTip>
                </td>
                <td className={styles.measureColB}>
                  <ColTip tip={`${lapName} reached point B after traveling ${fmtFt(lapDistB, unitPrefs)} from lap start.`}>
                    {fmtFt(lapDistB, unitPrefs)}
                  </ColTip>
                </td>
                <td className={styles.measureColElapsed}>
                  <ColTip tip={`${lapName} traveled ${fmtFt(Math.abs(lapDistSpan), unitPrefs)} between A and B — reflects the actual line taken.`}>
                    {fmtFt(Math.abs(lapDistSpan), unitPrefs)}
                  </ColTip>
                </td>
                <td className={styles.measureColA}>
                  <ColTip tip={`${lapName} had been on track for ${fmtRelTime(timeAtA)} at point A.`}>
                    {fmtRelTime(timeAtA)}
                  </ColTip>
                </td>
                <td className={styles.measureColB}>
                  <ColTip tip={`${lapName} had been on track for ${fmtRelTime(timeAtB)} at point B.`}>
                    {fmtRelTime(timeAtB)}
                  </ColTip>
                </td>
                <td className={styles.measureColElapsed}>
                  <ColTip tip={elapsedTip}>
                    {elapsed != null ? elapsed.toFixed(3) + ' s' : '—'}
                  </ColTip>
                </td>
                <td className={styles.measureColA}>
                  <ColTip tip={aTip}>{eA.delta > 0 ? '+' : ''}{eA.delta.toFixed(3)} s</ColTip>
                </td>
                <td className={styles.measureColB}>
                  <ColTip tip={bTip}>{eB.delta > 0 ? '+' : ''}{eB.delta.toFixed(3)} s</ColTip>
                </td>
                <td className={diffClass}>
                  <ColTip tip={gainLossTip}>{diffStr}</ColTip>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function TimeDeltaTrace({
  lapTraces, selectedLaps,
  compareDrivers = [],
  driverName1 = '',
  customColors1 = null,
  height,
  anchoredTooltip = false,
  baseOverride = null,
  onBaseOverrideChange = null,
  laps = null,
  unitPrefs = {},
  trackName = '',
  sessionDate = '',
  sessionVehicle = '',
}) {
  const { chartColors } = useTheme();
  const cornerHl = useCornerHighlight();
  // Trend coloring default: on for a single comparison lap (identity is
  // unambiguous), off for multiple (lap colors matter). null = no manual pick.
  const [trendOverride,  setTrendOverride]  = useState(null);
  const [zoomDomain,     setZoomDomain]     = useState(null);
  const [isSelecting,    setIsSelecting]    = useState(false);
  const [selectionViz,   setSelectionViz]   = useState(null);
  const [isAnnotateMode, setIsAnnotateMode] = useState(false);
  const [naturalExtent, setNaturalExtent] = useState(null);
  const [tooltipData,   setTooltipData]   = useState(null);
  const [wrapWidth,     setWrapWidth]     = useState(null);
  const [wrapHeight,    setWrapHeight]    = useState(null);
  const [measureMode,   setMeasureMode]   = useState(false);
  const [measurePoints, setMeasurePoints] = useState([]);
  const [baseDotX,      setBaseDotX]      = useState(null);
  const [showSpeed,     setShowSpeed]     = useState(false);
  const [zeroAtZoom,   setZeroAtZoom]   = useState(false);

  const measureModeRef   = useRef(false);

  const chartWrapRef      = useRef(null);
  const hoverLineRef      = useRef(null);
  const zoomDomainRef        = useRef(null);
  const naturalExtentRef     = useRef(null);
  const seriesRef            = useRef([]);
  const baseCsvTimeLookupRef = useRef([]);
  const isSelectingRef    = useRef(false);
  const isAnnotateModeRef = useRef(false);
  const isDirectHoverRef  = useRef(false);
  const tooltipSideRef    = useRef('right');
  const refAreaLeft       = useRef(null);
  const refAreaRight      = useRef(null);
  const finalizeZoomRef   = useRef(null);
  const onPanFtRef        = useRef(null);
  const onPinchZoomRef    = useRef(null);
  const showSpeedRef      = useRef(false);
  // Right-side exclusion zone: 12 normally, 48 when speed axis (36 wide) is shown
  const rightPadRef       = useRef(12);

  // ── Find base lap (globally lowest lap_time_seconds) ──────────────────────
  // Resolve a comparison driver by `session` value (slot+2). Returns null for
  // the primary driver (session 1).
  const cmpForSession = (session) => compareDrivers.find(d => d.slot === session - 2) || null;

  const { baseLookup, baseCsvTimeLookup, baseLapInfo } = useMemo(() => {
    const base = pickBaseLapMulti(lapTraces, selectedLaps, compareDrivers, baseOverride);
    if (!base) return { baseLookup: [], baseCsvTimeLookup: [], baseLapInfo: null };

    const { trace, lapIndex, session } = base;
    const cd = session === 1 ? null : cmpForSession(session);
    const info = {
      lapIndex, session,
      color: cd
        ? getLapColor(lapIndex, cd.selectedLaps, cd.palette, cd.customColors)
        : getLapColor(lapIndex, selectedLaps,    LAP_COLORS,  customColors1),
      label: trace.lap_time_str || `L${trace.lap_number}`,
    };
    const rawLookup = buildLookup(trace.points);
    // buildLookup carries `time`, which is seconds *into the lap*. The video
    // panel seeks on the session clock, so shift by the lap's start — without
    // it every click lands near the start of the video.
    const lapStartTime = trace.start_time ?? 0;
    const csvLookup = lapStartTime
      ? rawLookup.map(p => ({ ...p, relTime: p.relTime + lapStartTime }))
      : rawLookup;
    return { baseLookup: normalizeLookup(rawLookup), baseCsvTimeLookup: csvLookup, baseLapInfo: info };
  }, [lapTraces, selectedLaps, compareDrivers, customColors1, baseOverride]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Build delta series for each non-base selected lap ────────────────────
  const series = useMemo(() => {
    if (!baseLookup.length || !baseLapInfo) return [];
    const result = [];

    const applyZeroOffset = (points) => {
      if (!zeroAtZoom || !zoomDomain || !points.length) return points;
      const offset = points[0].delta;
      if (offset === 0) return points;
      return points.map(p => ({ ...p, delta: Math.round((p.delta - offset) * 1000) / 1000 }));
    };

    for (const idx of selectedLaps) {
      const tr = lapTraces[idx];
      if (!tr) continue;
      if (baseLapInfo.session === 1 && idx === baseLapInfo.lapIndex) continue;
      const points = applyZeroOffset(buildDeltaSeries(baseLookup, tr, zoomDomain));
      if (!points.length) continue;
      result.push({
        key: `s1-${idx}`,
        session: 1,
        lapIndex: idx,
        label: idx === TB_LAP_INDEX ? `TB ${tr.lap_time_str || ''}`.trim() : (tr.lap_time_str || `L${tr.lap_number}`),
        driverName: driverName1,
        color: idx === TB_LAP_INDEX ? TB_COLOR : getLapColor(idx, selectedLaps, LAP_COLORS, customColors1),
        isTb: idx === TB_LAP_INDEX,
        points,
      });
    }

    for (const d of compareDrivers) {
      const session = d.slot + 2;
      for (const idx of d.selectedLaps) {
        const tr = d.lapTraces[idx];
        if (!tr) continue;
        if (baseLapInfo.session === session && idx === baseLapInfo.lapIndex) continue;
        const points = applyZeroOffset(buildDeltaSeries(baseLookup, tr, zoomDomain));
        if (!points.length) continue;
        result.push({
          key: `s${session}-${idx}`,
          session,
          lapIndex: idx,
          label: tr.lap_time_str || `L${tr.lap_number}`,
          driverName: d.name,
          color: getLapColor(idx, d.selectedLaps, d.palette, d.customColors),
          points,
        });
      }
    }

    return result;
  }, [baseLookup, baseLapInfo, lapTraces, selectedLaps, compareDrivers, zoomDomain, zeroAtZoom, customColors1]);

  // Flat Δ=0 line for the base lap — rendered when no other series exist (single-lap measure mode)
  const baseFlatLine = useMemo(() => {
    if (!baseLookup.length || !baseLapInfo) return [];
    const source = zoomDomain
      ? baseLookup.filter(p => {
          const ft = p.distM * M_TO_FT;
          return ft >= zoomDomain[0] && ft <= zoomDomain[1];
        })
      : baseLookup;
    return downsample(source.length ? source : baseLookup, MAX_PTS).map(p => ({
      dist_ft: Math.round(p.distM * M_TO_FT),
      delta: 0,
    }));
  }, [baseLookup, baseLapInfo, zoomDomain]);

  useEffect(() => { seriesRef.current  = series; },  [series]);
  useEffect(() => { baseCsvTimeLookupRef.current = baseCsvTimeLookup; }, [baseCsvTimeLookup]);
  useEffect(() => { showSpeedRef.current  = showSpeed;  rightPadRef.current = showSpeed ? 48 : 12; }, [showSpeed]);

  // GPS points for the base lap — used for position sync when series is empty (single-lap mode)
  const baseGpsPointsRef = useRef([]);
  const baseFlatLineRef  = useRef([]);
  useEffect(() => { baseFlatLineRef.current = baseFlatLine; }, [baseFlatLine]);
  useEffect(() => {
    if (!baseLapInfo) { baseGpsPointsRef.current = []; return; }
    const baseTraces = baseLapInfo.session === 1
      ? lapTraces
      : (cmpForSession(baseLapInfo.session)?.lapTraces ?? {});
    const tr = baseTraces[baseLapInfo.lapIndex];
    baseGpsPointsRef.current = (tr?.points ?? [])
      .filter(p => p.dist_m != null && p.lat && p.lon)
      .map(p => ({ distFt: Math.round(p.dist_m * M_TO_FT), lat: p.lat, lon: p.lon }))
      .sort((a, b) => a.distFt - b.distFt);
  }, [baseLapInfo, lapTraces, compareDrivers]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Speed overlay series (all laps including base) ────────────────────────
  const speedSeries = useMemo(() => {
    if (!showSpeed) return [];
    const result = [];

    const buildSpeedPts = (tr) => {
      if (!tr?.points) return [];
      const raw = tr.points.filter(p => p.dist_m != null && p.speed != null);
      if (!raw.length) return [];
      const source = zoomDomain
        ? raw.filter(p => { const ft = p.dist_m * M_TO_FT; return ft >= zoomDomain[0] && ft <= zoomDomain[1]; })
        : raw;
      return downsample(source.length ? source : raw, MAX_PTS).map(p => ({
        dist_ft: Math.round(p.dist_m * M_TO_FT),
        speed:   Math.round(p.speed * 10) / 10,
      }));
    };

    // Base lap
    if (baseLapInfo) {
      const baseTraces = baseLapInfo.session === 1
        ? lapTraces
        : (cmpForSession(baseLapInfo.session)?.lapTraces ?? {});
      const pts = buildSpeedPts(baseTraces[baseLapInfo.lapIndex]);
      if (pts.length) result.push({ key: 'spd-base', color: baseLapInfo.color, points: pts });
    }

    // Primary (session 1) non-base laps
    for (const idx of selectedLaps) {
      if (baseLapInfo?.session === 1 && idx === baseLapInfo.lapIndex) continue;
      const tr = lapTraces[idx];
      const pts = buildSpeedPts(tr);
      if (!pts.length) continue;
      result.push({
        key: `spd-s1-${idx}`,
        color: getLapColor(idx, selectedLaps, LAP_COLORS, customColors1),
        points: pts,
      });
    }

    // Comparison-driver non-base laps
    for (const d of compareDrivers) {
      const session = d.slot + 2;
      for (const idx of d.selectedLaps) {
        if (baseLapInfo?.session === session && idx === baseLapInfo.lapIndex) continue;
        const tr = d.lapTraces[idx];
        const pts = buildSpeedPts(tr);
        if (!pts.length) continue;
        result.push({
          key: `spd-s${session}-${idx}`,
          color: getLapColor(idx, d.selectedLaps, d.palette, d.customColors),
          points: pts,
        });
      }
    }

    return result;
  }, [showSpeed, baseLapInfo, lapTraces, selectedLaps, compareDrivers,
      zoomDomain, customColors1]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track wrapper dimensions for tooltip flip and dot positioning
  useEffect(() => {
    const wrap = chartWrapRef.current;
    if (!wrap) return;
    const ro = new ResizeObserver(() => {
      setWrapWidth(wrap.clientWidth);
      setWrapHeight(wrap.clientHeight);
    });
    ro.observe(wrap);
    setWrapWidth(wrap.clientWidth);
    setWrapHeight(wrap.clientHeight);
    return () => ro.disconnect();
  }, []);

  // Subscribe to SpeedTrace's x-extent
  useEffect(() => {
    return subscribeExtent(ext => {
      naturalExtentRef.current = ext;
      setNaturalExtent(ext);
    });
  }, []);

  // Sync zoom with other charts
  // Sync zoom with the shared bus — receives current value immediately on mount.
  useEffect(() => {
    return subscribeZoom(domain => {
      setZoomDomain(domain);
      zoomDomainRef.current = domain;
      if (!domain) setZeroAtZoom(false);
    });
  }, []);

  useEffect(() => {
    const handler = (e) => {
      const active = !!e.detail?.active;
      setIsAnnotateMode(active);
      isAnnotateModeRef.current = active;
    };
    window.addEventListener('trackapp:annotate-mode', handler);
    return () => window.removeEventListener('trackapp:annotate-mode', handler);
  }, []);

  useEffect(() => {
    const handler = () => finalizeZoomRef.current?.();
    window.addEventListener('mouseup', handler);
    return () => window.removeEventListener('mouseup', handler);
  }, []);

  // ── Touch gesture support ─────────────────────────────────────────────────
  const LEFT_PAD  = 44;
  const RIGHT_PAD = 12;

  const pixelToDistFt = (clientX) => {
    const wrap = chartWrapRef.current;
    if (!wrap) return null;
    const zoom = zoomDomainRef.current;
    const nat  = naturalExtentRef.current;
    const serDists = seriesRef.current.flatMap(s => s.points.map(p => p.dist_ft));
    const allDists = serDists.length ? serDists : baseFlatLineRef.current.map(p => p.dist_ft);
    if (!allDists.length) return null;
    const xMin = zoom ? zoom[0] : (nat?.[0] ?? arrayMin(allDists));
    const xMax = zoom ? zoom[1] : (nat?.[1] ?? arrayMax(allDists));
    if (xMax === xMin) return null;
    const rect   = wrap.getBoundingClientRect();
    const chartW = rect.width - LEFT_PAD - rightPadRef.current;
    if (chartW <= 0) return null;
    const t = Math.max(0, Math.min(1, (clientX - rect.left - LEFT_PAD) / chartW));
    return xMin + t * (xMax - xMin);
  };
  const pixelToDistFtRef = useRef(pixelToDistFt);
  pixelToDistFtRef.current = pixelToDistFt;

  const interpolateBaseCsvTime = (distFt) => {
    const distM = distFt / M_TO_FT;
    return interpolateRelTime(baseCsvTimeLookupRef.current, distM);
  };

  const findNearestBaseGps = (distFt) => {
    const pts = baseGpsPointsRef.current;
    if (!pts.length) return null;
    let best = pts[0], bestDiff = Math.abs(pts[0].distFt - distFt);
    for (let i = 1; i < pts.length; i++) {
      const d = Math.abs(pts[i].distFt - distFt);
      if (d < bestDiff) { best = pts[i]; bestDiff = d; }
    }
    return best;
  };

  const onCrosshairMove = (clientX) => {
    const wrap = chartWrapRef.current;
    if (!wrap) return;
    const distFt = pixelToDistFtRef.current(clientX);
    if (distFt == null) return;
    const rect   = wrap.getBoundingClientRect();
    const chartW = wrap.clientWidth - LEFT_PAD - RIGHT_PAD;
    const xPx    = LEFT_PAD + Math.max(0, Math.min(chartW, clientX - rect.left - LEFT_PAD));

    const line = hoverLineRef.current;
    if (line) { line.style.left = `${xPx}px`; line.style.display = 'block'; }

    const entries = buildTooltipEntries(distFt);
    if (entries.length) {
      const ww = chartWrapRef.current?.clientWidth ?? 600;
      const frac = xPx / ww;
      if (frac < 0.45) tooltipSideRef.current = 'right';
      else if (frac > 0.55) tooltipSideRef.current = 'left';
      setTooltipData({ x: xPx, distFt, entries, side: tooltipSideRef.current });
    } else {
      setTooltipData(null);
    }
    const first = entries[0];
    if (first?.lat && first?.lon) {
      window.dispatchEvent(new CustomEvent('trackapp:chart-hover', {
        detail: { lat: first.lat, lon: first.lon, distFt, videoTime: interpolateBaseCsvTime(distFt) },
      }));
    } else {
      const gps = findNearestBaseGps(distFt);
      if (gps) {
        window.dispatchEvent(new CustomEvent('trackapp:chart-hover', {
          detail: { lat: gps.lat, lon: gps.lon, distFt, videoTime: interpolateBaseCsvTime(distFt) },
        }));
      }
    }
  };
  const onCrosshairMoveRef = useRef(onCrosshairMove);
  onCrosshairMoveRef.current = onCrosshairMove;

  const onCrosshairClear = () => {
    isDirectHoverRef.current = false;
    setTooltipData(null);
    if (hoverLineRef.current) hoverLineRef.current.style.display = 'none';
    window.dispatchEvent(new CustomEvent('trackapp:chart-hover', { detail: null }));
  };
  const onCrosshairClearRef = useRef(onCrosshairClear);
  onCrosshairClearRef.current = onCrosshairClear;

  useTouchChartGestures({
    chartWrapRef,
    pixelToDistFtRef,
    onCrosshairMoveRef,
    onCrosshairClearRef,
    isSelectingRef,
    refAreaLeft,
    refAreaRight,
    setIsSelecting,
    setSelectionViz,
    finalizeZoomRef,
    isDirectHoverRef,
    onPanFtRef,
    onPinchZoomRef,
  });

  // Helper: pixel x position for a given distFt
  function distFtToXPx(distFt, wrap) {
    const zoom = zoomDomainRef.current;
    const nat  = naturalExtentRef.current;
    const serDists = seriesRef.current.flatMap(s => s.points.map(p => p.dist_ft));
    const allDists = serDists.length ? serDists : baseFlatLineRef.current.map(p => p.dist_ft);
    if (!allDists.length) return null;
    const xMin = zoom ? zoom[0] : (nat?.[0] ?? arrayMin(allDists));
    const xMax = zoom ? zoom[1] : (nat?.[1] ?? arrayMax(allDists));
    if (xMax === xMin) return null;
    if (zoom && (distFt < xMin || distFt > xMax)) return null;
    const chartW = wrap.clientWidth - 44 - rightPadRef.current;
    const t = Math.max(0, Math.min(1, (distFt - xMin) / (xMax - xMin)));
    return 44 + t * chartW;
  }

  // Build tooltip entries from all series at a given distFt
  function buildTooltipEntries(distFt) {
    return seriesRef.current.map(s => {
      const pt = findNearest(s.points, distFt);
      if (!pt) return null;
      return { key: s.key, label: s.label, driverName: s.driverName, color: s.color, delta: pt.delta, dist_ft: pt.dist_ft, lat: pt.lat, lon: pt.lon };
    }).filter(Boolean);
  }

  // Map hover → hover line + tooltip (when another chart is the source)
  useEffect(() => {
    const handler = (e) => {
      const line = hoverLineRef.current;
      const wrap = chartWrapRef.current;
      if (!wrap) return;

      const distFt = e.detail?.distFt;
      if (distFt == null) {
        if (line) line.style.display = 'none';
        if (!isDirectHoverRef.current) setTooltipData(null);
        return;
      }

      const xPx = distFtToXPx(distFt, wrap);
      if (xPx == null) {
        if (line) line.style.display = 'none';
        if (!isDirectHoverRef.current) setTooltipData(null);
        return;
      }

      if (line) { line.style.left = `${xPx}px`; line.style.display = 'block'; }

      if (!isDirectHoverRef.current) {
        const entries = buildTooltipEntries(distFt);
        if (entries.length) {
          const ww = wrap.clientWidth ?? 600;
          const frac = xPx / ww;
          if (frac < 0.45) tooltipSideRef.current = 'right';
          else if (frac > 0.55) tooltipSideRef.current = 'left';
          setTooltipData({ x: xPx, distFt, entries, side: tooltipSideRef.current });
        } else {
          setTooltipData(null);
        }
      }
    };
    window.addEventListener('trackapp:map-hover', handler);
    return () => window.removeEventListener('trackapp:map-hover', handler);
  }, []);

  // Direct chart-hover from another trace panel
  useEffect(() => {
    const handler = (e) => {
      if (isDirectHoverRef.current) return;
      const distFt = e.detail?.distFt;
      const line   = hoverLineRef.current;
      const wrap   = chartWrapRef.current;
      if (distFt == null) {
        setTooltipData(null);
        if (line) line.style.display = 'none';
        return;
      }
      if (!wrap) return;
      const xPx = distFtToXPx(distFt, wrap);
      if (xPx == null) { if (line) line.style.display = 'none'; return; }
      if (line) { line.style.left = `${xPx}px`; line.style.display = 'block'; }
      const entries = buildTooltipEntries(distFt);
      if (entries.length) {
        const ww = wrap.clientWidth ?? 600;
        const frac = xPx / ww;
        if (frac < 0.45) tooltipSideRef.current = 'right';
        else if (frac > 0.55) tooltipSideRef.current = 'left';
        setTooltipData({ x: xPx, distFt, entries, side: tooltipSideRef.current });
      } else {
        setTooltipData(null);
      }
    };
    window.addEventListener('trackapp:chart-hover', handler);
    // Late mount (mobile tab switch): show the last cursor position.
    const last = lastCursor();
    if (last) handler({ detail: last });
    return () => window.removeEventListener('trackapp:chart-hover', handler);
  }, []);

  const toggleMeasureMode = () => {
    const next = !measureModeRef.current;
    measureModeRef.current = next;
    setMeasureMode(next);
    if (!next) setMeasurePoints([]);
  };

  // ── Zoom drag / measure click ─────────────────────────────────────────────
  const handleMouseDown = (e) => {
    if (e?.activeLabel == null) return;
    const x = Number(e.activeLabel);
    // In measure mode we still start the drag machinery so a drag zooms normally.
    // A click (lo === hi in finalizeZoom) will place a measure point instead.
    refAreaLeft.current  = x;
    refAreaRight.current = x;
    isSelectingRef.current = true;
    setIsSelecting(true);
    setSelectionViz({ left: x, right: x });
  };

  const finalizeZoom = () => {
    if (!isSelectingRef.current) return;
    isSelectingRef.current = false;
    setIsSelecting(false);

    const l = refAreaLeft.current;
    const r = refAreaRight.current;
    setSelectionViz(null);
    refAreaLeft.current  = null;
    refAreaRight.current = null;

    if (l == null || r == null) return;
    const [lo, hi] = l <= r ? [l, r] : [r, l];
    if (lo === hi) {
      if (measureModeRef.current) {
        // Click (not drag) in measure mode → place a measure point
        const entries = buildTooltipEntries(lo);
        const pt = { distFt: lo, entries };
        setMeasurePoints(prev => prev.length >= 2 ? [pt] : [...prev, pt]);
      } else {
        const entries = buildTooltipEntries(lo);
        const first = entries[0];
        if (first?.lat && first?.lon) {
          window.dispatchEvent(new CustomEvent('trackapp:chart-click', {
            detail: { lat: first.lat, lon: first.lon, distFt: lo, videoTime: interpolateBaseCsvTime(lo) },
          }));
        }
      }
      return;
    }

    if (isAnnotateModeRef.current) {
      window.dispatchEvent(new CustomEvent('trackapp:annotation-captured', {
        detail: { channel: 'time_delta', channelLabel: 'Time Delta', distFtStart: lo, distFtEnd: hi },
      }));
      setIsAnnotateMode(false);
      isAnnotateModeRef.current = false;
      window.dispatchEvent(new CustomEvent('trackapp:annotate-mode', { detail: { active: false } }));
    }
    publishZoom([lo, hi]);
    window.dispatchEvent(new CustomEvent('trackapp:chart-zoom', {
      detail: { distFtMin: lo, distFtMax: hi },
    }));
  };
  finalizeZoomRef.current = finalizeZoom;

  const resetZoom = () => {
    publishZoom(null);
    window.dispatchEvent(new CustomEvent('trackapp:chart-zoom', { detail: null }));
  };

  const dispatchZoom = (domain) => {
    publishZoom(domain);
    window.dispatchEvent(new CustomEvent('trackapp:chart-zoom', {
      detail: { distFtMin: domain[0], distFtMax: domain[1] },
    }));
  };

  // Pan delta handler — shifts zoomDomain by deltaFt, clamped to natural extent
  const onPanFt = (deltaFt) => {
    const zoom = zoomDomainRef.current;
    if (!zoom) return;
    const nat    = naturalExtentRef.current;
    const natMin = nat ? nat[0] : zoom[0];
    const natMax = nat ? nat[1] : zoom[1];
    const size   = zoom[1] - zoom[0];
    let lo = zoom[0] + deltaFt;
    let hi = zoom[1] + deltaFt;
    if (lo < natMin) { hi = natMin + size; lo = natMin; }
    if (hi > natMax) { lo = natMax - size; hi = natMax; }
    dispatchZoom([lo, hi]);
  };
  onPanFtRef.current = onPanFt;

  const onPinchZoom = (scaleFactor, centerFt) => {
    const nat    = naturalExtentRef.current;
    const zoom   = zoomDomainRef.current;
    const natMin = nat ? nat[0] : null;
    const natMax = nat ? nat[1] : null;
    let lo = zoom ? zoom[0] : natMin;
    let hi = zoom ? zoom[1] : natMax;
    if (lo == null || hi == null) return;
    const center = centerFt != null ? Math.max(lo, Math.min(hi, centerFt)) : (lo + hi) / 2;
    const newLo  = Math.max(natMin ?? lo, center - (center - lo) / scaleFactor);
    const newHi  = Math.min(natMax ?? hi, center + (hi - center) / scaleFactor);
    if (newHi - newLo < 200) return;
    dispatchZoom([newLo, newHi]);
  };
  onPinchZoomRef.current = onPinchZoom;

  usePanOnWheel({ chartWrapRef, zoomDomainRef, leftPad: LEFT_PAD, rightPadRef, onPanFtRef });

  // Direct chart hover — compute tooltip from all series via findNearest
  const handleMouseMove = (data) => {
    isDirectHoverRef.current = true;
    if (isSelectingRef.current) {
      if (data?.activeLabel != null) {
        refAreaRight.current = Number(data.activeLabel);
        setSelectionViz({ left: refAreaLeft.current, right: refAreaRight.current });
      }
      return;
    }

    const coord  = data?.activeCoordinate;
    const distFt = data?.activeLabel != null ? Number(data.activeLabel) : null;
    if (!coord || distFt == null) {
      setTooltipData(null);
      return;
    }

    if (!seriesRef.current.length) {
      // Single-lap mode: sync other panels and show a tracking dot
      const gps = findNearestBaseGps(distFt);
      if (gps) {
        window.dispatchEvent(new CustomEvent('trackapp:chart-hover', {
          detail: { lat: gps.lat, lon: gps.lon, distFt, videoTime: interpolateBaseCsvTime(distFt) },
        }));
      }
      setBaseDotX(coord.x);
      return;
    }

    const entries = buildTooltipEntries(distFt);
    if (entries.length) {
      const ww = chartWrapRef.current?.clientWidth ?? 600;
      const frac = coord.x / ww;
      if (frac < 0.45) tooltipSideRef.current = 'right';
      else if (frac > 0.55) tooltipSideRef.current = 'left';
      setTooltipData({ x: coord.x, distFt, entries, side: tooltipSideRef.current });
    } else {
      setTooltipData(null);
    }

    const first = entries[0];
    if (first?.lat && first?.lon) {
      window.dispatchEvent(new CustomEvent('trackapp:chart-hover', {
        detail: { lat: first.lat, lon: first.lon, distFt, videoTime: interpolateBaseCsvTime(distFt) },
      }));
    }
  };

  const handleMouseLeave = () => {
    isDirectHoverRef.current = false;
    if (isSelectingRef.current) {
      isSelectingRef.current = false;
      setIsSelecting(false);
      setSelectionViz(null);
      refAreaLeft.current  = null;
      refAreaRight.current = null;
    }
    setTooltipData(null);
    setBaseDotX(null);
    window.dispatchEvent(new CustomEvent('trackapp:chart-hover', { detail: null }));
  };

  const totalSelected = selectedLaps.length + compareDrivers.reduce((n, d) => n + d.selectedLaps.length, 0);
  const isEmpty = series.length === 0;

  const allDeltas = series.flatMap(s => s.points.map(p => p.delta));
  const absMax  = allDeltas.length ? arrayMax(allDeltas.map(Math.abs)) : 2;
  const yMax    = Math.ceil(absMax / 0.5) * 0.5 || 1;
  const yDomain = [-yMax, yMax];

  const allSpeeds  = speedSeries.flatMap(s => s.points.map(p => p.speed));
  const speedYMax  = allSpeeds.length ? Math.ceil(arrayMax(allSpeeds) / 20) * 20 : 160;
  const rightMargin = showSpeed ? 48 : 12;

  const allDistsFt = series.flatMap(s => s.points.map(p => p.dist_ft));
  const baseDistsFt = isEmpty ? baseFlatLine.map(p => p.dist_ft) : [];
  const xDomainComputed = zoomDomain
    || naturalExtent
    || arrayExtent(allDistsFt.concat(baseDistsFt))
    || ['auto', 'auto'];

  // Single-lap comparison gets a gain/loss fill (skip TB — it's a synthetic lap)
  const fillSeries = series.filter(s => !s.isTb);

  const trendColor = trendOverride ?? (fillSeries.length === 1);

  // Slope-colored strokes: green where gaining on the base, red where losing,
  // white where even — same scale as the map ribbon (full saturation at
  // 0.005 s/m). Gradient stops are fractions of each line's own x-extent,
  // which matches the SVG objectBoundingBox mapping. With several laps this
  // trades lap-identity colors for trend, so the Trend button can toggle it
  // (hover dots and tooltips keep lap colors either way).
  const buildSlopeStops = (pts) => {
    if (pts.length < 8) return null;
    const xMin = pts[0].dist_ft;
    const xMax = pts[pts.length - 1].dist_ft;
    const span = xMax - xMin;
    if (!(span > 0)) return null;

    const FULL = 0.005 / M_TO_FT;   // s per ft at full saturation
    const N    = Math.min(140, Math.max(40, Math.floor(pts.length / 4)));
    const seg  = span / N;

    let j = 0;
    const deltaAt = (x) => {
      while (j < pts.length - 2 && pts[j + 1].dist_ft <= x) j++;
      const a = pts[j], b = pts[j + 1];
      if (b.dist_ft === a.dist_ft) return a.delta;
      const t = Math.max(0, Math.min(1, (x - a.dist_ft) / (b.dist_ft - a.dist_ft)));
      return a.delta + t * (b.delta - a.delta);
    };

    const stops = [];
    let prev = deltaAt(xMin);
    for (let i = 1; i <= N; i++) {
      const d = deltaAt(xMin + i * seg);
      const t = Math.max(-1, Math.min(1, ((d - prev) / seg) / FULL));
      prev = d;
      const color = t >= 0
        ? `rgb(255,${Math.round((1 - t) * 255)},${Math.round((1 - t) * 255)})`
        : `rgb(${Math.round((1 + t) * 255)},255,${Math.round((1 + t) * 255)})`;
      stops.push({ offset: (i - 0.5) / N, color });
    }
    return stops;
  };

  const slopeGradients = trendColor
    ? fillSeries
        .map(s => ({ key: s.key, stops: buildSlopeStops(s.points) }))
        .filter(g => g.stops)
    : [];

  const selectionColor = isAnnotateMode ? '#4f9cf5' : '#f5c842';
  const zoomArea = selectionViz && selectionViz.left !== selectionViz.right ? (
    <ReferenceArea
      yAxisId="dt"
      x1={Math.min(selectionViz.left, selectionViz.right)}
      x2={Math.max(selectionViz.left, selectionViz.right)}
      fill={selectionColor} fillOpacity={0.15}
      stroke={selectionColor} strokeOpacity={0.6}
    />
  ) : null;

  const chartMargin = { top: 8, right: rightMargin, bottom: 4, left: 0 };

  const handleExportPng = useCallback(() => {
    const svg = chartWrapRef.current?.querySelector('svg.recharts-surface')
      || chartWrapRef.current?.querySelector('svg');
    if (!svg || !baseLapInfo) return;

    const driverForSession = (sessionNumber) => {
      if (sessionNumber === 1) {
        return {
          name: driverName1 || 'Driver 1',
          date: sessionDate,
          vehicle: sessionVehicle,
          laps,
          traces: lapTraces,
        };
      }
      const driver = compareDrivers.find(item => item.slot === sessionNumber - 2);
      return {
        name: driver?.name || `Driver ${sessionNumber}`,
        date: driver?.session?.meta?.date || '',
        vehicle: driver?.session?.meta?.vehicle || '',
        laps: driver?.laps,
        traces: driver?.lapTraces || {},
      };
    };

    const entries = [
      {
        session: baseLapInfo.session,
        lapIndex: baseLapInfo.lapIndex,
        color: baseLapInfo.color,
        isBase: true,
      },
      ...series.map(item => ({
        session: item.session,
        lapIndex: item.lapIndex,
        color: item.color,
        isBase: false,
      })),
    ];
    const drivers = entries.map(item => driverForSession(item.session));
    const dates = new Set(drivers.map(driver => driver.date).filter(Boolean));
    const vehicles = new Set(drivers.map(driver => driver.vehicle).filter(Boolean));
    const mixedDates = dates.size > 1;
    const mixedVehicles = vehicles.size > 1;
    const uniformDate = mixedDates ? '' : [...dates][0] || '';
    const uniformVehicle = mixedVehicles ? '' : [...vehicles][0] || '';

    const lapLabelFor = (item, driver) => {
      const trace = driver.traces?.[item.lapIndex];
      const lapName = item.lapIndex === TB_LAP_INDEX
        ? 'TB'
        : `L${lapDisplayNumber(driver.laps, item.lapIndex, trace?.lap_number)}`;
      return [lapName, trace?.lap_time_str].filter(Boolean).join(' · ');
    };
    const legend = entries.map((item, index) => {
      const driver = drivers[index];
      return {
        color: item.color,
        label: `${driver.name} — ${lapLabelFor(item, driver)}${item.isBase ? ' · BASE' : ''}${
          mixedVehicles && driver.vehicle ? ` · ${driver.vehicle}` : ''}${
          mixedDates && driver.date ? ` · ${driver.date}` : ''}`,
      };
    });

    const baseDriver = driverForSession(baseLapInfo.session);
    const baseLabel = lapLabelFor({ lapIndex: baseLapInfo.lapIndex }, baseDriver);
    const subtitle = [
      'Δ Time vs Distance',
      `Base: ${baseDriver.name} ${baseLabel}`,
      zoomDomain ? 'Zoomed' : null,
      zeroAtZoom ? 'Relative' : null,
      trendColor ? 'Trend colors' : null,
      showSpeed ? 'Speed overlay' : null,
    ].filter(Boolean).join(' · ');
    const slug = value => String(value || '')
      .replace(/[^\w-]+/g, '_')
      .replace(/^_+|_+$/g, '');
    const host = window.location.hostname;

    exportChartPng({
      svg,
      themeFrom: chartWrapRef.current,
      title: [trackName, uniformVehicle, uniformDate].filter(Boolean).join(' · ') || 'Time Delta',
      subtitle,
      legend,
      plotTop: CHART_TOP,
      plotBottom: CHART_BOTTOM,
      watermark: /^(localhost|127\.|\d+\.\d+\.)/.test(host) ? 'TrackDay Analysis' : host,
      filename: `${[
        slug(trackName) || 'track',
        slug(uniformDate),
        'timedelta',
      ].filter(Boolean).join('_')}.png`,
    }).catch(error => console.error('Chart export failed:', error));
  }, [baseLapInfo, compareDrivers, driverName1, lapTraces, laps, series, sessionDate,
      sessionVehicle, showSpeed, trackName, trendColor, zeroAtZoom, zoomDomain]);

  return (
    <div className={styles.panel} style={height != null ? { height, minHeight: height } : { height: '100%' }}>
      <PanelHeader className={styles.header} title={<span className={styles.title}>Time Delta</span>}>
        <BasePicker
          baseLapInfo={baseLapInfo}
          baseOverride={baseOverride}
          onChange={onBaseOverrideChange}
          lapTraces={lapTraces} selectedLaps={selectedLaps} laps={laps}
          compareDrivers={compareDrivers}
        />
        <div className={styles.opts}>
          <button
            type="button"
            className={styles.optBtn}
            onClick={handleExportPng}
            title="Download this time-delta chart as a PNG image with the base lap and driver / lap-time legend"
          >PNG ⤓</button>
          <button
            className={`${styles.optBtn} ${trendColor ? styles.optActive : ''}`}
            onClick={() => setTrendOverride(!trendColor)}
            title="Color delta lines by trend — green gaining on base, red losing (off = lap colors). Defaults on for a single comparison lap."
          >Trend</button>
          <button
            className={`${styles.optBtn} ${showSpeed ? styles.optActive : ''}`}
            onClick={() => setShowSpeed(v => !v)}
            title="Overlay speed trace for each lap"
          >Speed</button>
          {zoomDomain && (
            <button
              className={`${styles.optBtn} ${zeroAtZoom ? styles.optActive : ''}`}
              onClick={() => setZeroAtZoom(v => !v)}
              title="When zoomed, reset delta to zero at the left edge of the zoom window"
            >Relative</button>
          )}
          <button
            className={`${styles.optBtn} ${measureMode ? styles.optMeasure : ''}`}
            onClick={toggleMeasureMode}
            title="Click two points on the trace to measure time delta between them"
          >Measure</button>
          <HelpButton panel="timedeltatrace" page="sessions" className={styles.optBtn} />
        </div>
      </PanelHeader>

      <div
        className={styles.chartWrap}
        ref={chartWrapRef}
        onDoubleClick={resetZoom}
        style={{
          position: 'relative',
          cursor: measureMode
            ? 'crosshair'
            : isSelecting
              ? (isAnnotateMode ? 'crosshair' : 'ew-resize')
              : (isAnnotateMode ? 'crosshair' : 'col-resize'),
          userSelect: 'none',
          outline: isAnnotateMode ? '2px solid #4f9cf5' : 'none',
          outlineOffset: '-2px',
        }}
      >
        {isEmpty && !(measureMode && baseLapInfo) ? (
          <div className={styles.empty}>
            {totalSelected < 2
              ? 'Select 2 or more laps to see time delta, or select Measure for single lap'
              : 'Loading trace data…'}
          </div>
        ) : (
          <>
            <div
              ref={hoverLineRef}
              style={{
                display: 'none', position: 'absolute',
                top: 8, bottom: 4, width: 1,
                background: '#f5c842', opacity: 0.85,
                pointerEvents: 'none', zIndex: 10,
              }}
            />
            <HoverDots data={tooltipData} yMin={-yMax} yMax={yMax} wrapHeight={wrapHeight} />
            {isEmpty && baseDotX != null && wrapHeight != null && baseLapInfo && (() => {
              const chartH = wrapHeight - CHART_TOP - CHART_BOTTOM;
              const yPx    = CHART_TOP + chartH / 2; // delta=0 is always vertical centre
              const color  = baseLapInfo.color;
              return (
                <div style={{
                  position: 'absolute', pointerEvents: 'none', zIndex: 15,
                  left: baseDotX - 5, top: yPx - 5,
                  width: 10, height: 10, borderRadius: '50%',
                  background: color, border: '2px solid #fff',
                  boxShadow: `0 0 4px ${color}88`,
                }} />
              );
            })()}
            <HoverTooltip data={tooltipData} wrapWidth={wrapWidth} anchoredTooltip={anchoredTooltip} unitPrefs={unitPrefs} />
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                margin={chartMargin}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={finalizeZoom}
                onMouseLeave={handleMouseLeave}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                <XAxis
                  dataKey="dist_ft"
                  type="number"
                  domain={xDomainComputed}
                  tickFormatter={v => `${(convertDistanceFt(v, unitPrefs) / 1000).toFixed(1)}k`}
                  stroke={chartColors.axis}
                  tick={{ fill: chartColors.tick, fontSize: 10 }}
                  allowDuplicatedCategory={false}
                  label={{ value: `${getDistanceUnit(unitPrefs)} from lap start`, position: 'insideBottomRight', offset: -4, fill: chartColors.label, fontSize: 9 }}
                />
                <YAxis
                  yAxisId="dt"
                  domain={yDomain}
                  stroke={chartColors.axis}
                  tick={{ fill: chartColors.tick, fontSize: 10 }}
                  tickFormatter={v => v > 0 ? `+${v.toFixed(1)}` : v.toFixed(1)}
                  label={{ value: 'Δ sec', angle: -90, position: 'insideLeft', offset: 16, fill: chartColors.label, fontSize: 10 }}
                  width={44}
                />

                {showSpeed && (
                  <YAxis
                    yAxisId="speed"
                    orientation="right"
                    domain={[0, speedYMax]}
                    stroke={chartColors.axis}
                    tick={{ fill: chartColors.tick, fontSize: 10 }}
                    label={{ value: 'mph', angle: 90, position: 'insideRight', offset: -6, fill: chartColors.label, fontSize: 10 }}
                    width={36}
                  />
                )}

                <ReferenceLine yAxisId="dt" y={0} stroke="#3a5a3a" strokeWidth={1.5} strokeDasharray="4 2" />

                {/* Gain/loss fill vs the zero line — only with a single comparison
                    lap, where "the delta" is unambiguous. Green below zero =
                    ahead of base, red above = behind (timing convention). */}
                <defs>
                  {/* Zero sits at 50% because yDomain is symmetric */}
                  <linearGradient id="tdGainLossFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0"   stopColor="#ff4040" stopOpacity={0.25} />
                    <stop offset="0.5" stopColor="#ff4040" stopOpacity={0.03} />
                    <stop offset="0.5" stopColor="#3ddc6a" stopOpacity={0.03} />
                    <stop offset="1"   stopColor="#3ddc6a" stopOpacity={0.25} />
                  </linearGradient>
                  {slopeGradients.map(({ key, stops }) => (
                    <linearGradient key={key} id={`tdSlope-${key}`} x1="0" y1="0" x2="1" y2="0">
                      {stops.map((s, i) => (
                        <stop key={i} offset={s.offset} stopColor={s.color} />
                      ))}
                    </linearGradient>
                  ))}
                </defs>

                {/* Gain/loss fill — only with a single comparison lap, where
                    "the delta" is unambiguous */}
                {fillSeries.length === 1 && (
                  <Area
                    yAxisId="dt"
                    data={fillSeries[0].points}
                    dataKey="delta"
                    baseValue={0}
                    stroke="none"
                    fill="url(#tdGainLossFill)"
                    isAnimationActive={false}
                    activeDot={false}
                  />
                )}

                {/* Speed overlay — drawn first so delta lines render on top */}
                {showSpeed && speedSeries.map(({ key, color, points }) => (
                  <Line
                    key={key}
                    yAxisId="speed"
                    data={points}
                    dataKey="speed"
                    stroke={color}
                    strokeWidth={1.5}
                    strokeOpacity={0.4}
                    dot={false}
                    isAnimationActive={false}
                    activeDot={false}
                  />
                ))}

                {series.map(({ key, color, points, isTb }) => {
                  const slopeColored = !isTb && slopeGradients.some(g => g.key === key);
                  return (
                    <Line
                      key={key}
                      yAxisId="dt"
                      data={points}
                      dataKey="delta"
                      name={key}
                      stroke={slopeColored ? `url(#tdSlope-${key})` : color}
                      strokeWidth={slopeColored ? 2 : isTb ? 2 : 1.5}
                      strokeDasharray={isTb ? '6 4' : undefined}
                      dot={false}
                      isAnimationActive={false}
                      activeDot={false}
                    />
                  );
                })}

                {isEmpty && baseFlatLine.length > 0 && (
                  <Line
                    key="base-flat"
                    yAxisId="dt"
                    data={baseFlatLine}
                    dataKey="delta"
                    name="base-flat"
                    stroke={baseLapInfo.color}
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                    activeDot={false}
                  />
                )}

                {zoomArea}

                {/* Corner highlight from the Corner Metrics panel */}
                {cornerHl?.distFtMin != null && (
                  <ReferenceArea
                    yAxisId="dt"
                    x1={cornerHl.distFtMin}
                    x2={cornerHl.distFtMax}
                    fill="#f5c842"
                    fillOpacity={0.07}
                    stroke="#f5c842"
                    strokeOpacity={0.35}
                    strokeDasharray="4 3"
                  />
                )}

                {measurePoints[0] && (
                  <ReferenceLine
                    yAxisId="dt"
                    x={measurePoints[0].distFt}
                    stroke="#a0c4ff"
                    strokeWidth={1.5}
                  />
                )}
                {measurePoints[1] && (
                  <ReferenceLine
                    yAxisId="dt"
                    x={measurePoints[1].distFt}
                    stroke="#ffd580"
                    strokeWidth={1.5}
                    strokeDasharray="5 3"
                  />
                )}
                {measurePoints.length === 2 && (
                  <ReferenceArea
                    yAxisId="dt"
                    x1={Math.min(measurePoints[0].distFt, measurePoints[1].distFt)}
                    x2={Math.max(measurePoints[0].distFt, measurePoints[1].distFt)}
                    fill="#ffffff"
                    fillOpacity={0.03}
                  />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </>
        )}
      </div>
      <MeasureReadout points={measurePoints} series={series} baseLookup={baseLookup} baseLapInfo={baseLapInfo} unitPrefs={unitPrefs} />
    </div>
  );
}
